import { useLayoutEffect, useState } from 'react';

const RESIZE_DEBOUNCE_MS = 100;

/**
 * Sets cssVarName to the component's measured height, for a screen's own percent-of-container
 * sizing helper. Also returns the height so children can list it as an effect dependency —
 * child effects run before parent ones, so they'd measure against the 100vh fallback without
 * this re-trigger.
 */
export function useContainerHeightVar(ref, cssVarName) {
  const [heightPx, setHeightPx] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const applyHeight = () => {
      // Skip writes at 0 height (hidden ancestor) so the variable keeps its last value.
      const { height } = el.getBoundingClientRect();
      if (height > 0) {
        el.style.setProperty(cssVarName, `${height}px`);
        setHeightPx(height);
      }
    };
    applyHeight();

    let debounceTimer = null;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyHeight, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(el);

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [ref, cssVarName]);

  return heightPx;
}
